import React from "react";

import { isAssignmentReassignable } from "./ReassignModal";
import {
  formatAssignedSince,
  formatTeamViewsLastLogin,
  personRoleLabel,
  teamViewsAvatarColor,
  teamViewsRoleBadgeClass,
} from "./teamViewsHelpers";
import type { TeamViewsPerson } from "../../../Store/Reducers/TeamViewsSlice";
import { getInitials } from "../../../Utils/Helper/Helper";

type TeamViewsPersonRowProps = {
  person: TeamViewsPerson;
  showPortfolioHighlight?: boolean;
  /** Logged-in user — show a green "You" chip beside the role badge. */
  isYou?: boolean;
  /** Privileged viewers only (VA / C-Level / ESL / executiveOps). */
  canManageTeamCoverage?: boolean;
  onReassignPerson?: (person: TeamViewsPerson) => void;
  onReassignAccount?: (person: TeamViewsPerson, accountId: string) => void;
  onHistoryAccount?: (person: TeamViewsPerson, accountId: string) => void;
};

/** By Person list row: collapsed summary + expanded assigned-accounts grid. */
const TeamViewsPersonRow: React.FC<TeamViewsPersonRowProps> = ({
  person,
  showPortfolioHighlight: _showPortfolioHighlight = false,
  isYou = false,
  canManageTeamCoverage = false,
  onReassignPerson,
  onReassignAccount,
  onHistoryAccount,
}) => {
  const displayName = person.full_name || "Unknown";
  const roleLabel = personRoleLabel(person);
  const assignments = person.assignments || [];
  const accountCount = person.account_count ?? assignments.length;
  const canReassign =
    canManageTeamCoverage && assignments.some(isAssignmentReassignable);

  return (
    <details
      className={`tv-person${canManageTeamCoverage ? "" : " tv-person--no-actions"}`}
    >
      <summary className="tv-person-summary">
        <span className="tv-person-cell tv-person-cell-account">
          <span className="tv-person-identity">
            <span
              className="tv-person-avatar"
              style={{ background: teamViewsAvatarColor(person) }}
            >
              {getInitials(displayName)}
            </span>
            <span className="tv-person-identity-text">
              <span className="tv-person-name-row">
                <span className="tv-person-name">{displayName}</span>
                {/* Portfolio pill hidden while Show my team exists — restore if needed:
                {showPortfolioHighlight && person.overlaps_viewer_portfolio ? (
                  <span className="tv-portfolio-pill">Your portfolio</span>
                ) : null}
                */}
              </span>
              {canManageTeamCoverage ? (
                <span
                  className={`tv-person-login${person.last_login_at ? "" : " tv-not-logged-in"}`}
                >
                  {formatTeamViewsLastLogin(person.last_login_at)}
                </span>
              ) : null}
            </span>
          </span>
        </span>
        <span className="tv-person-cell tv-person-cell-role">
          {roleLabel || isYou ? (
            <span className="tv-card-role-row tv-card-role-row--inline">
              {roleLabel ? (
                <span
                  className={`tv-person-role ${teamViewsRoleBadgeClass(roleLabel)}`.trim()}
                >
                  {roleLabel}
                </span>
              ) : null}
              {isYou ? (
                <span className="tv-you-badge" title="You">
                  You
                </span>
              ) : null}
            </span>
          ) : (
            <span className="tv-person-col-empty">—</span>
          )}
        </span>
        <span className="tv-person-cell tv-person-cell-since">
          <span className="tv-person-count">
            {accountCount} account{accountCount === 1 ? "" : "s"}
          </span>
        </span>
        {canManageTeamCoverage ? (
          <span className="tv-person-cell tv-person-cell-actions">
            {canReassign ? (
              <button
                type="button"
                className="tv-person-reassign"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReassignPerson?.(person);
                }}
              >
                Reassign / Remove
              </button>
            ) : null}
          </span>
        ) : null}
      </summary>

      {assignments.length ? (
        <div className="tv-person-accounts">
          <div className="tv-person-accounts-hdr" aria-hidden="true">
            <span className="tv-person-cell tv-person-cell-account">
              Account
            </span>
            <span className="tv-person-cell tv-person-cell-role">Role</span>
            <span className="tv-person-cell tv-person-cell-since">
              Assigned since
            </span>
            {canManageTeamCoverage ? (
              <span className="tv-person-cell tv-person-cell-actions">
                Actions
              </span>
            ) : null}
          </div>
          {assignments.map(assignment => (
            <div className="tv-person-accounts-row" key={assignment.account_id}>
              <span className="tv-person-cell tv-person-cell-account">
                {assignment.account_name || "—"}
              </span>
              <span className="tv-person-cell tv-person-cell-role">
                {assignment.role_name || "—"}
              </span>
              <span className="tv-person-cell tv-person-cell-since">
                {formatAssignedSince(assignment.assigned_since)}
              </span>
              {canManageTeamCoverage ? (
                <span className="tv-person-cell tv-person-cell-actions">
                  <span className="tv-person-row-actions">
                    <button
                      type="button"
                      className="tv-person-row-action"
                      onClick={() =>
                        onHistoryAccount?.(person, assignment.account_id)
                      }
                    >
                      History
                    </button>
                    {isAssignmentReassignable(assignment) ? (
                      <button
                        type="button"
                        className="tv-person-row-action"
                        onClick={() =>
                          onReassignAccount?.(person, assignment.account_id)
                        }
                      >
                        Reassign
                      </button>
                    ) : (
                      <span className="tv-person-row-locked">Locked</span>
                    )}
                  </span>
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="tv-person-empty">No accounts assigned.</p>
      )}
    </details>
  );
};

export default TeamViewsPersonRow;
